import type { documentation } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * Pure presentation logic for the documentation records surface (doc 01 A5.2).
 * Kept free of React so the mapping between a documentation state and what a
 * reviewer sees is pinned by unit tests — the screen itself never invents a
 * state label or a colour.
 */

export type DocState = documentation.DocumentationState;

export interface DocFilter {
  state?: DocState;
  isLateOnly?: boolean;
}

/** The five documentation lifecycle states, in review order. */
export const DOC_STATES: DocState[] = ['undocumented', 'draft', 'late', 'complete', 'void'];

export function stateLabel(state: DocState): string {
  switch (state) {
    case 'undocumented':
      return 'Not started';
    case 'draft':
      return 'In progress';
    case 'late':
      return 'Late';
    case 'complete':
      return 'Complete';
    case 'void':
      return 'Void';
  }
}

export function statePillVariant(state: DocState): Severity {
  switch (state) {
    case 'complete':
      return 'good';
    case 'draft':
      return 'sky';
    case 'late':
      return 'urgent';
    case 'void':
      return 'plain';
    case 'undocumented':
      return 'unsure';
  }
}

/** "Today", "Yesterday", "3d ago" — the age a reviewer reads a queue by. */
export function ageLabel(occurredAt: string, now: Date = new Date()): string {
  const occurred = new Date(occurredAt);
  const dayMs = 24 * 3_600_000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfOccurred = new Date(
    occurred.getFullYear(),
    occurred.getMonth(),
    occurred.getDate(),
  ).getTime();
  const days = Math.round((startOfToday - startOfOccurred) / dayMs);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

/** Several attributed staff and no single author: a group session, not "nobody". */
export function groupSessionLabel(staff: number): string {
  return `Group session (${staff} staff)`;
}

/** A record is open when it still owes a note (complete/void owe nothing). */
export function isOpen(state: DocState): boolean {
  return state !== 'complete' && state !== 'void';
}

/**
 * The filter pills the screen shows. Every pill maps to a real server-side
 * filter — there are no client-only decorations that imply filtering the
 * backend did not perform.
 */
export interface FilterPill {
  key: string;
  label: string;
  active: (f: DocFilter) => boolean;
  next: (f: DocFilter) => DocFilter;
}

export const FILTER_PILLS: FilterPill[] = [
  {
    key: 'open',
    label: 'Open',
    active: (f) => !f.state && !f.isLateOnly,
    next: () => ({ state: undefined, isLateOnly: false }),
  },
  {
    // NOT 'late': `DOC_STATES` below contains the state `'late'` and is spread
    // into this same list, so both pills rendered under the React key `late` and
    // every component test printed "Encountered two children with the same key".
    // The two are different filters — this one sets `isLateOnly`, the state pill
    // sets `state` — so they need different identities. The key is only ever a
    // React key and a test handle; no URL or API reads it.
    key: 'late-only',
    label: 'Late only',
    active: (f) => f.isLateOnly === true,
    next: (f) => ({ state: f.state, isLateOnly: f.isLateOnly ? false : true }),
  },
  ...DOC_STATES.map((s): FilterPill => ({
    key: s,
    label: stateLabel(s),
    active: (f) => f.state === s,
    next: (f) => ({ state: f.state === s ? undefined : s, isLateOnly: f.isLateOnly }),
  })),
];

// ---------------------------------------------------------------------------
// The reviewer half of the authority model
// ---------------------------------------------------------------------------

/**
 * WHAT A REVIEWER MAY DO TO A RECORD, AND WHY THIS TABLE IS HERE AT ALL.
 *
 * `is_late` is a STORED input on `service_documentation_status`, not a derived
 * one: `boolean NOT NULL DEFAULT false`, with no CHECK tying it to
 * `state = 'late'` and no trigger computing it. The only writers are the
 * reviewer branches of `PUT /services/events/:id/documentation` and
 * `POST .../documentation/transition`. No admin screen called either, so in a
 * live tenant the column was `false` on every row — and `docs_overdue`, which
 * is literally `docs.filter((d) => d.is_late).length`
 * (`widgetDataService.ts:resolveDocsOverdue`), read a PERMANENT ZERO that an
 * organisation reads as a clean compliance record. The queue's own "Late only",
 * "Late" and "Void" pills could only ever return nothing, for the same reason.
 *
 * (It demoed correctly, which is the worse failure mode: `scripts/demo-seed.mjs`
 * INSERTs `is_late` and `state = 'late'` by direct SQL, so the KPI is only ever
 * zero where it matters.)
 *
 * THE MOVES ARE DERIVED FROM THE SERVER'S OWN TABLE, not retyped as a wish.
 * `DOC_TRANSITIONS` in `apps/api/src/domains/services/defaults.ts` is:
 *
 *   undocumented → draft, complete, void
 *   draft        → draft, complete, late, void
 *   complete     → late, void
 *   late         → complete, void
 *   void         → (nothing; terminal)
 *
 * and `SELF_SERVICE_STATES` is `{draft, complete}`, so `late` and `void` are
 * the two a worker may never set for themselves. Offering a move outside this
 * table would be a button that can only ever produce a 422.
 */
export type ReviewerActionKind = 'mark-late' | 'clear-late' | 'void';

export interface ReviewerAction {
  kind: ReviewerActionKind;
  /** The button's words. A verb about the record, never about the column. */
  label: string;
  /** The state this move lands in. */
  state: DocState;
  /**
   * The lateness verdict to send with it, or undefined to leave it alone.
   *
   * Sent EXPLICITLY on both directions rather than left to follow the state.
   * Nothing in the schema ties the two, so `state = 'complete'` with
   * `is_late = true` is a representable row and is exactly what "this was
   * finished, and it was finished late" means. Clearing the verdict therefore
   * has to say `false` out loud; omitting it preserves whatever was there.
   */
  isLate?: boolean;
  /**
   * Whether this move is irreversible and must be confirmed before it is sent.
   * `void` is terminal in the server's transition table — there is no move OUT
   * of it — so it is the one action here that cannot be taken back.
   */
  irreversible: boolean;
  /** What the reviewer is agreeing to, shown at the confirmation. */
  consequence?: string;
}

/**
 * The reviewer moves legal FROM this state, in the order they should render.
 *
 * An empty list is a real answer, not a missing feature: `void` is terminal,
 * and `undocumented` has no lateness verdict to pass because there is no status
 * row to move (`transitionDocumentation` throws `NotFoundError` when none
 * exists — see the screen, which says so rather than showing a dead button).
 */
export function reviewerActions(state: DocState): ReviewerAction[] {
  const voidAction: ReviewerAction = {
    kind: 'void',
    label: 'Void this obligation',
    state: 'void',
    irreversible: true,
    consequence:
      'Voiding cancels the documentation this visit owes. It is the last state — there is no ' +
      'move out of it, and no way to reopen the obligation afterwards.',
  };
  const markLate: ReviewerAction = {
    kind: 'mark-late',
    label: 'Mark this note late',
    state: 'late',
    isLate: true,
    irreversible: false,
  };
  const clearLate: ReviewerAction = {
    kind: 'clear-late',
    label: 'Not late after all',
    state: 'complete',
    isLate: false,
    irreversible: false,
  };

  switch (state) {
    case 'draft':
    case 'complete':
      return [markLate, voidAction];
    case 'late':
      return [clearLate, voidAction];
    case 'undocumented':
      return [voidAction];
    case 'void':
      return [];
  }
}
